import React, { useEffect, useState } from "react";
import moment from "moment";
import { isEqual, trim } from "lodash";
import {
  BookingSupportTagFormDataFragment,
  BookingSupportTagStatusFragment,
  useBookingProfile_GetTagsQuery,
  BookingTagsFragment
} from "@graphql";
import locale from "@bonliva-crm/locale";
import Select, { Option } from "@components/Select";
import { Typography, TextField, Box } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { useSnackbars } from "@hooks";

type Props = {
  initialData: BookingSupportTagFormDataFragment;
  bookingOrKit?: "booking" | "bookingKit";
  onSubmit: (
    id: string,
    comment: string,
    tags: string[]
  ) => Promise<BookingSupportTagStatusFragment[]>;
  loading: boolean;
};

export function BookingSupportTagForm(props: Props) {
  const { showSuccess, showError } = useSnackbars();

  const [initialTags, setInitialTags] = useState<string[]>([]);
  const [initialComment, setInitialComment] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [comment, setComment] = useState("");

  const { data: tagsData } = useBookingProfile_GetTagsQuery({
    fetchPolicy: "cache-first"
  });

  useEffect(() => {
    const tags = props.initialData?.bookingSupportTagStatus?.map((x) => x.tag) ?? [];

    setInitialTags(tags);
    setTags(tags);
    setOptions(
      getOptions(
        props.initialData?.bookingSupportTagStatus,
        tagsData?.config,
        props.bookingOrKit
      )
    );
    setInitialComment(props.initialData?.bookingSupportTagComment);
    setComment(props.initialData?.bookingSupportTagComment);
  }, [props.initialData, tagsData?.config]);

  const handleSubmit = () => {
    props
      .onSubmit(props.initialData._id, comment, tags)
      .then((res) => {
        showSuccess("Updated");
        setInitialTags(tags);
        setInitialComment(comment);
        setOptions(getOptions(res, tagsData.config, props.bookingOrKit));
      })
      .catch((err) => showError(`${err}`));
  };

  const canSubmit =
    props.loading ||
    !isEqual(tags, initialTags) ||
    trim(comment) !== trim(initialComment);

  return (
    <div>
      <Box>
        <Select
          label={locale.ManagementStatus}
          multi
          options={options}
          onChange={(v) => setTags(v)}
          value={tags}
          fullWidth
          name={`bookingTagStatus`}
          multiType={"chips"}
          transformValue={(v) => v.value}
          disabled={props.loading}
        />
      </Box>

      <Box mt={1}>
        <TextField
          label={locale.Notes}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          fullWidth
          multiline
          disabled={props.loading}
        />
      </Box>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <Button
          color={"primary"}
          size={"small"}
          variant={"contained"}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {locale.Save}
        </Button>
      </div>
    </div>
  );
}

export const getOptions = (
  statuses: BookingSupportTagStatusFragment[],
  allTags: BookingTagsFragment,
  bookingOrKit?: "booking" | "bookingKit"
): Option[] => {
  const tags = allTags?.bookingTags ?? [];

  return tags
    .filter((x) => x[bookingOrKit] === true)
    .filter((x) => x.code !== "Empty")
    .filter((x) => !x.code.startsWith("Exclude_"))
    .map((x) => {
      const status = statuses?.find((s) => s.tag === x.code);
      return {
        label: x.label,
        value: x.code,
        description: status ? (
          <>
            <Typography variant={"caption"}>{status.setBy?.Name}</Typography>{" "}
            <Typography variant={"caption"}>
              {moment(status?.setOn).format("YYYY-MM-DD HH:mm")}
            </Typography>
          </>
        ) : null,
        icon: (
          <span
            style={{
              display: "inline-block",
              background: x.color,
              width: 16,
              height: 16,
              borderRadius: "50%",
              marginRight: 2
            }}
          />
        )
      };
    });
};
